/**
 * LibraryPickerCard — the visual peer of QuestionCard for library reuse.
 *
 * The Director's `list_library_items` tool returns a UserQuestion flavored as a
 * library picker (`metadata.render_as === 'library_picker'`), which renders here
 * instead of the plain text QuestionCard. It shows the user's saved items as a
 * compact selectable grid (thumbnail / waveform / script snippet); selecting one
 * answers the question with that item's `node_id`, which routes back to the
 * Director (peer of ask_user_question) so it can call use_from_library and wire
 * the item per the conversation. Selection does NOT go straight to canvas.
 */
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { getLibraryThumbnailUrl } from '../../api/useAssetUrl'
import type { ChatEventLibraryPickerItem } from '../../hooks/useChatSocket'

export interface LibraryPickerCardProps {
  question: string
  items: ChatEventLibraryPickerItem[]
  selectedNodeId?: string
  onSelect: (nodeId: string) => void
  disabled?: boolean
}

function ItemThumb({ item }: { item: ChatEventLibraryPickerItem }) {
  const { t } = useTranslation('chat')
  const cls = 'w-full aspect-square rounded-md object-cover bg-black/30'
  if (item.out_image) {
    return <img src={getLibraryThumbnailUrl(item.out_image) ?? undefined} alt={item.label ?? ''} className={cls} loading="lazy" />
  }
  if (item.out_video) {
    return <video src={getLibraryThumbnailUrl(item.out_video) ?? undefined} preload="metadata" muted className={cls} />
  }
  // Non-visual (script / audio): a text/descriptor tile so it's still
  // disambiguable without a thumbnail.
  return (
    <div
      className="w-full aspect-square rounded-md bg-surface-2 border border-white/5 p-2 overflow-hidden text-left"
      data-testid={`library-pick-text-${item.node_id}`}
    >
      <p className="text-xxs leading-snug text-text-muted line-clamp-6 whitespace-pre-wrap break-words">
        {item.descriptor || item.label || (item.kind ?? t('libraryPicker.item'))}
      </p>
    </div>
  )
}

export const LibraryPickerCard = memo(function LibraryPickerCard({
  question,
  items,
  selectedNodeId,
  onSelect,
  disabled = false,
}: LibraryPickerCardProps) {
  const { t } = useTranslation('chat')
  return (
    <div className="rounded-lg border border-border bg-surface-1 p-3" data-testid="library-picker-card">
      <p className="text-sm text-text-secondary mb-2">{question}</p>
      <div className="grid grid-cols-4 gap-2" data-testid="library-picker-grid">
        {items.map((item) => {
          const isSelected = selectedNodeId === item.node_id
          return (
            <button
              key={item.node_id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(item.node_id)}
              data-testid={`library-pick-${item.node_id}`}
              className={`group relative flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all ${
                isSelected
                  ? 'border-chrome/60 bg-chrome/5 ring-1 ring-chrome/30'
                  : 'border-white/5 hover:border-border hover:bg-surface-2'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <ItemThumb item={item} />
              <span className="text-xs font-medium text-text-secondary truncate w-full text-center">
                {item.label || item.node_type || t('libraryPicker.untitled')}
              </span>
              {item.descriptor && !item.out_image && !item.out_video ? null : (
                item.descriptor ? (
                  <span className="text-xxs text-text-muted truncate w-full text-center">
                    {item.descriptor}
                  </span>
                ) : null
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
})
